// „Přidat +" menu na profilu hráče: DNA pool / Player Pool / focus / vlastní seznamy.
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Siren, Stack, Binoculars, ListPlus, Check, CaretRight } from "@phosphor-icons/react";
import { useSharedPool, POOL_MAX } from "../lib/timing-pool-store";
import { listFocuses, addCandidate, addToPool, getLists, createList, addListPlayer, removeListPlayer } from "../lib/lab-api";

export default function AddPlayerMenu({ player }) {
    const { addPlayer, removePlayer, hasPlayer, pool } = useSharedPool();
    const [open, setOpen] = useState(false);
    const [sub, setSub] = useState(null);
    const [focuses, setFocuses] = useState([]);
    const [lists, setLists] = useState([]);
    const [newList, setNewList] = useState("");
    const ref = useRef(null);

    useEffect(() => {
        if (!open) { setSub(null); return; }
        listFocuses().then((d) => setFocuses((d.items || []).filter((f) => !["archived", "completed"].includes(f.status)))).catch(() => {});
        getLists().then((d) => setLists(d.items || [])).catch(() => {});
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
    }, [open]);

    if (!player?.fpl_id) return null;
    const inDna = hasPlayer(player.fpl_id);

    const toggleDna = () => {
        if (inDna) { removePlayer(player.fpl_id); toast.success("Odebrán z Hlídače"); return; }
        if (pool.length >= POOL_MAX) { toast.error(`Hlídač je plný (max ${POOL_MAX} hráčů)`); return; }
        addPlayer({
            fpl_id: player.fpl_id, display_name: player.name, historical_slug: player.player_key || null,
            position: player.position, team: player.team, current_price: player.price ?? null,
            ownership_pct: player.ownership_pct ?? 0, seasons_available: 0, match_confidence: 100,
        });
        toast.success(`${player.name} → Hlídač`);
    };

    const toPool = async () => {
        try {
            await addToPool({ fpl_id: player.fpl_id, status: "watch", source: "player_profile" });
            toast.success(`${player.name} → Player Pool`);
        } catch (e) { toast.error(e.response?.data?.detail || "Hráč už je v Player Poolu"); }
    };

    const toFocus = async (f) => {
        try {
            const d = await addCandidate(f.id, { fpl_id: player.fpl_id, source: "player_profile" });
            toast.success(`${player.name} → kandidát „${f.title}" (${d.active_count}/${d.max_active})`);
        } catch (e) { toast.error(e.response?.data?.detail || "Zařazení selhalo"); }
    };

    const inList = (l) => (l.players || []).some((p) => p.fpl_id === player.fpl_id);

    const toggleList = async (l) => {
        try {
            if (inList(l)) { await removeListPlayer(l.id, player.fpl_id); toast.success(`Odebrán ze „${l.name}"`); }
            else { await addListPlayer(l.id, player.fpl_id); toast.success(`${player.name} → „${l.name}"`); }
            const d = await getLists(); setLists(d.items || []);
        } catch (e) { toast.error(e.response?.data?.detail || "Změna selhala"); }
    };

    const makeList = async () => {
        if (!newList.trim()) return;
        try {
            await createList({ name: newList.trim(), fpl_id: player.fpl_id });
            toast.success(`Seznam „${newList.trim()}" vytvořen s hráčem ${player.name}`);
            setNewList("");
            const d = await getLists(); setLists(d.items || []);
        } catch { toast.error("Vytvoření selhalo"); }
    };

    const itemCls = "w-full text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest flex items-center gap-2 hover:bg-white/5 transition-colors";

    return (
        <div className="relative" ref={ref}>
            <button type="button" onClick={() => setOpen(!open)} data-testid="add-player-menu-btn"
                    className="font-mono text-[10px] uppercase tracking-widest px-4 py-2 border border-fa-yellow/60 text-fa-yellow hover:bg-fa-yellow hover:text-black flex items-center gap-1.5 transition-colors">
                <Plus size={12} weight="bold" /> Přidat…
            </button>
            {open && (
                <div className="absolute z-40 top-full left-0 mt-1 w-72 bg-[#111] border border-white/20 shadow-xl" data-testid="add-player-menu">
                    <button type="button" onClick={toggleDna} className={itemCls} data-testid="add-menu-dna">
                        <Siren size={13} style={{ color: "#FF8800" }} /> Hlídač <span className="text-white/35 font-mono text-[9px]">(sledování + Season DNA)</span>
                        {inDna && <Check size={12} className="text-fa-mint ml-auto" />}
                    </button>
                    <button type="button" onClick={toPool} className={itemCls} data-testid="add-menu-pool">
                        <Stack size={13} className="text-fa-mint" /> Player Pool (celý)
                    </button>
                    <button type="button" onClick={() => setSub(sub === "focus" ? null : "focus")} className={itemCls} data-testid="add-menu-focus">
                        <Binoculars size={13} className="text-fa-sky" /> Do focusu <CaretRight size={11} className="ml-auto" />
                    </button>
                    {sub === "focus" && (
                        <div className="border-t border-white/10 max-h-40 overflow-y-auto bg-black/40">
                            {focuses.length === 0 && <p className="px-3 py-2 font-mono text-[9px] text-white/40">Žádný aktivní focus</p>}
                            {focuses.map((f) => (
                                <button key={f.id} type="button" onClick={() => toFocus(f)} data-testid={`add-menu-focus-${f.id}`}
                                        className="w-full text-left px-4 py-1.5 font-mono text-[10px] hover:bg-white/5 truncate">
                                    {f.title} <span className="text-white/35">({f.candidate_count || 0}/11)</span>
                                </button>
                            ))}
                        </div>
                    )}
                    <button type="button" onClick={() => setSub(sub === "lists" ? null : "lists")} className={itemCls} data-testid="add-menu-lists">
                        <ListPlus size={13} className="text-fa-cream" /> Do seznamu <CaretRight size={11} className="ml-auto" />
                    </button>
                    {sub === "lists" && (
                        <div className="border-t border-white/10 bg-black/40">
                            <div className="max-h-36 overflow-y-auto">
                                {lists.map((l) => (
                                    <button key={l.id} type="button" onClick={() => toggleList(l)} data-testid={`add-menu-list-${l.id}`}
                                            className="w-full text-left px-4 py-1.5 font-mono text-[10px] hover:bg-white/5 flex items-center gap-2">
                                        <span className="truncate">{l.name}</span>
                                        <span className="text-white/35">({(l.players || []).length})</span>
                                        {inList(l) && <Check size={11} className="text-fa-mint ml-auto flex-shrink-0" />}
                                    </button>
                                ))}
                            </div>
                            <div className="flex gap-1 p-2 border-t border-white/10">
                                <input value={newList} onChange={(e) => setNewList(e.target.value)}
                                       onKeyDown={(e) => e.key === "Enter" && makeList()}
                                       placeholder="Nový seznam…" data-testid="add-menu-new-list"
                                       className="flex-1 bg-black/40 border border-white/20 px-2 py-1 font-mono text-[10px] text-white focus:outline-none focus:border-fa-mint placeholder:text-white/35" />
                                <button type="button" onClick={makeList} data-testid="add-menu-create-list"
                                        className="font-mono text-[9px] px-2 border border-fa-mint/50 text-fa-mint hover:bg-fa-mint hover:text-black">
                                    <Plus size={11} />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
